/**
 * Single source of truth for deployment provenance.
 * Populated by vite.config.ts at build time.
 */
export interface BuildInfo {
  commit: string;
  commitFull: string;
  branch: string;
  dirty: boolean;
  buildTime: string;
  buildEnv: string;
  nodeVersion: string;
}

export const BUILD_INFO: BuildInfo =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  typeof __BUILD_INFO__ !== 'undefined' ? (__BUILD_INFO__ as any) : {
    commit: 'dev',
    commitFull: 'development',
    branch: 'local',
    dirty: true,
    buildTime: new Date().toISOString(),
    buildEnv: 'local',
    nodeVersion: 'n/a',
  };

/** Environment-derived runtime config. */
export const RUNTIME_INFO = {
  supabaseUrl: import.meta.env.VITE_SUPABASE_URL ?? null,
  appEnv: import.meta.env.VITE_APP_ENV ?? 'local',
  userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'server',
  online: typeof navigator !== 'undefined' ? navigator.onLine : true,
};

/** Format a build time as "Apr 15, 2026 14:23 UTC". */
export function formatBuildTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
      timeZone: 'UTC', timeZoneName: 'short',
    });
  } catch {
    return iso;
  }
}

/** Time elapsed since build (for freshness checks). */
export function ageOfBuild(): string {
  const built = new Date(BUILD_INFO.buildTime).getTime();
  const now = Date.now();
  const diffMs = now - built;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
